import type { Block, ContentAST, TextNode } from './ast/astTypes'
import { parseMarkdown } from './ast/markdownParser'
import { astToMarkdown } from './ast/markdownRenderer'
import { diffWords } from './diff/textDiff'

export interface ProposalWordChange {
  id: string
  blockIndex: number
  beforeStart: number
  beforeEnd: number
  afterStart: number
  afterEnd: number
  before: string
  after: string
}
export interface ProposalWordBlock {
  blockIndex: number
  before: TextNode[]
  after: TextNode[]
  changes: ProposalWordChange[]
}
export interface ProposalWordPlan {
  before: ContentAST
  after: ContentAST
  blocks: ProposalWordBlock[]
  changes: ProposalWordChange[]
}

export function sliceProposalText(content: TextNode[], start: number, end: number): TextNode[] {
  let offset = 0
  return content.flatMap((node) => {
    const from = Math.max(0, start - offset)
    const to = Math.min(node.text.length, end - offset)
    offset += node.text.length
    return to > from ? [{ ...node, text: node.text.slice(from, to) }] : []
  })
}
const plain = (content: TextNode[]) => content.map((node) => node.text).join('')
const textContent = (block: Block): TextNode[] | null =>
  (block.type === 'paragraph' || block.type === 'heading' || block.type === 'quote') &&
  block.content.every((node) => node.type === 'text')
    ? (block.content as TextNode[])
    : null

function sameFormatting(a: TextNode[], b: TextNode[]): boolean {
  const signature = (content: TextNode[]) => {
    const merged: Array<{ text: string; marks: string }> = []
    for (const node of content) {
      const marks = JSON.stringify(node.marks ?? [])
      const last = merged[merged.length - 1]
      if (last?.marks === marks) {
        last.text += node.text
      } else {
        merged.push({ text: node.text, marks })
      }
    }
    return JSON.stringify(merged)
  }
  return signature(a) === signature(b)
}

/** One replacement is one decision, even when its deleted and inserted words share spaces. */
export function proposalWordChanges(currentText: string, markdown: string): ProposalWordPlan | null {
  const before = parseMarkdown(currentText)
  const after = parseMarkdown(markdown)
  if (before.blocks.length !== after.blocks.length) {
    return null
  }
  const blocks: ProposalWordBlock[] = []
  const changes: ProposalWordChange[] = []
  for (let blockIndex = 0; blockIndex < before.blocks.length; blockIndex++) {
    const oldBlock = before.blocks[blockIndex]
    const newBlock = after.blocks[blockIndex]
    if (oldBlock.type !== newBlock.type) {
      return null
    }
    if (oldBlock.type === 'heading' && newBlock.type === 'heading' && oldBlock.level !== newBlock.level) {
      return null
    }
    const oldContent = textContent(oldBlock)
    const newContent = textContent(newBlock)
    if (!oldContent || !newContent) {
      const render = (block: Block) => astToMarkdown({ ...before, blocks: [block] }, { includeMetadata: false })
      if (render(oldBlock) !== render(newBlock)) {
        return null
      }
      continue
    }
    const oldText = plain(oldContent)
    const newText = plain(newContent)
    const runs = diffWords(oldText, newText)
    let oldOffset = 0
    let newOffset = 0
    const blockChanges: ProposalWordChange[] = []
    for (let i = 0; i < runs.length; i++) {
      const run = runs[i]
      if (run.op === 'same') {
        if (
          !sameFormatting(
            sliceProposalText(oldContent, oldOffset, oldOffset + run.text.length),
            sliceProposalText(newContent, newOffset, newOffset + run.text.length),
          )
        ) {
          return null
        }
        oldOffset += run.text.length
        newOffset += run.text.length
        continue
      }
      const beforeStart = oldOffset
      const afterStart = newOffset
      for (; i < runs.length; i++) {
        const part = runs[i]
        if (part.op === 'same' && (part.text.trim() || i === runs.length - 1)) {
          break
        }
        if (part.op !== 'insert') {
          oldOffset += part.text.length
        }
        if (part.op !== 'delete') {
          newOffset += part.text.length
        }
      }
      i--
      const change = {
        id: `${blockIndex}:${beforeStart}:${oldOffset}:${afterStart}:${newOffset}`,
        blockIndex,
        beforeStart,
        beforeEnd: oldOffset,
        afterStart,
        afterEnd: newOffset,
        before: oldText.slice(beforeStart, oldOffset),
        after: newText.slice(afterStart, newOffset),
      }
      blockChanges.push(change)
      changes.push(change)
    }
    if (blockChanges.length) {
      blocks.push({ blockIndex, before: oldContent, after: newContent, changes: blockChanges })
    }
  }
  return changes.length ? { before, after, blocks, changes } : null
}

/** Preserve the real block's id, attributes and all inline nodes outside this change. */
export function applyProposalWordChange(
  block: Block,
  plan: ProposalWordPlan,
  change: ProposalWordChange,
  accept: boolean,
): Block {
  const content = textContent(block)
  const donor = textContent((accept ? plan.after : plan.before).blocks[change.blockIndex])
  if (!content || !donor) {
    throw new Error('This change is no longer a text edit')
  }
  const start = accept ? change.beforeStart : change.afterStart
  const end = accept ? change.beforeEnd : change.afterEnd
  const donorStart = accept ? change.afterStart : change.beforeStart
  const donorEnd = accept ? change.afterEnd : change.beforeEnd
  return {
    ...block,
    content: [
      ...sliceProposalText(content, 0, start),
      ...sliceProposalText(donor, donorStart, donorEnd),
      ...sliceProposalText(content, end, Infinity),
    ],
  } as Block
}
